/**
 * Last-known tick cache (same key/format as fxbazzar_mobileview).
 * Persists LTP across refresh when API returns zeros.
 */

export const TICK_CACHE_PREFIX = "fxbazzar_tick_cache";

export function tickCacheStorageKey(userId) {
  return userId ? `${TICK_CACHE_PREFIX}_${userId}` : `${TICK_CACHE_PREFIX}_anon`;
}

export function normalizeTickKey(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

export function loadTickCache(userId) {
  try {
    const raw = localStorage.getItem(tickCacheStorageKey(userId));
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function cacheKeysForRow(row) {
  if (!row || typeof row !== "object") return [];
  const out = new Set();
  const add = (x) => {
    const k = normalizeTickKey(x);
    if (k) out.add(k);
    const base = k.split(/[0-9]/)[0];
    if (base && base.length >= 4) out.add(base);
    const first = String(x ?? "").trim().split(/\s+/)[0];
    const kFirst = normalizeTickKey(first);
    if (kFirst) out.add(kFirst);
  };
  add(row.displayName);
  add(row.script);
  add(row.symbol);
  add(row.name);
  add(row.Script);
  add(row.Symbol);
  return Array.from(out);
}

/** Restore LTP/Bid/Ask/prevClose from cache when API row has zeros. */
export function mergeRowWithTickCache(apiRow, cache) {
  if (!apiRow || typeof apiRow !== "object" || !cache) return apiRow;
  const apiLtp = Number(apiRow.lastTradedPrice ?? apiRow.ltp ?? apiRow.LTP ?? 0);
  const apiBid = Number(apiRow.buyPrice ?? apiRow.bid ?? 0);
  const apiAsk = Number(apiRow.sellPrice ?? apiRow.ask ?? 0);
  const needs =
    !Number.isFinite(apiLtp) ||
    apiLtp <= 0 ||
    !Number.isFinite(apiBid) ||
    apiBid <= 0 ||
    !Number.isFinite(apiAsk) ||
    apiAsk <= 0;
  if (!needs) return apiRow;

  for (const k of cacheKeysForRow(apiRow)) {
    const hit = cache[k];
    if (!hit || typeof hit !== "object") continue;
    const ltp = Number(hit.ltp ?? 0);
    if (!Number.isFinite(ltp) || ltp <= 0) continue;
    return {
      ...apiRow,
      lastTradedPrice: apiLtp > 0 ? apiRow.lastTradedPrice : ltp,
      buyPrice: apiBid > 0 ? apiRow.buyPrice : Number(hit.bid ?? apiBid ?? 0),
      sellPrice: apiAsk > 0 ? apiRow.sellPrice : Number(hit.ask ?? apiAsk ?? 0),
      close:
        Number(apiRow.close ?? 0) > 0
          ? apiRow.close
          : Number(hit.prevClose ?? hit.close ?? apiRow.close ?? 0),
      __stablePrevClose:
        Number(apiRow.__stablePrevClose ?? 0) > 0
          ? apiRow.__stablePrevClose
          : Number(hit.prevClose ?? hit.close ?? apiRow.__stablePrevClose ?? 0),
      changeAmount: hit.changeAmount ?? apiRow.changeAmount,
      changePercent: hit.changePercent ?? apiRow.changePercent,
      changeAmountColor: hit.changeAmountColor ?? apiRow.changeAmountColor,
    };
  }
  return apiRow;
}

/** Write index/watchlist tick to in-memory cache; optional persist callback debounced by caller. */
export function recordTickToCacheEntry(cache, row, extraKeys = []) {
  if (!cache || typeof cache !== "object") return cache;
  if (!row || typeof row !== "object") return cache;

  const ltp = Number(row.lastTradedPrice ?? row.ltp ?? row.LTP ?? 0);
  if (!Number.isFinite(ltp) || ltp <= 0) return cache;

  const bid = Number(row.buyPrice ?? row.bid ?? 0);
  const ask = Number(row.sellPrice ?? row.ask ?? 0);
  const prevClose = Number(row.__stablePrevClose ?? row.close ?? row.prevClose ?? 0);
  const keys = new Set();
  const add = (x) => {
    const k = normalizeTickKey(x);
    if (k) keys.add(k);
    const first = String(x ?? "").trim().split(/\s+/)[0];
    const kFirst = normalizeTickKey(first);
    if (kFirst) keys.add(kFirst);
  };
  add(row.displayName);
  add(row.script);
  add(row.symbol);
  add(row.name);
  extraKeys.forEach(add);

  const now = Date.now();
  keys.forEach((k) => {
    if (!k) return;
    cache[k] = {
      ltp,
      bid,
      ask,
      prevClose,
      changeAmount: row.changeAmount,
      changePercent: row.changePercent,
      changeAmountColor: row.changeAmountColor,
      ts: now,
    };
  });
  return cache;
}

export function persistTickCache(userId, cache) {
  try {
    localStorage.setItem(tickCacheStorageKey(userId), JSON.stringify(cache || {}));
  } catch {
    /* ignore quota */
  }
}
